'use client';

import React, { useState } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalCheckbox } from './GlobalCheckbox';
import { GlobalTextarea } from './GlobalTextarea';
import { useToast } from '../context/ToastProvider';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import { useUserProfile } from '../hooks/useUserProfile';
import LocalLLMTrust from './LocalLLMTrust';
import styles from './EntityGenerationSidebar.module.css';
import type { RecordType } from '../types';

interface TransformEntitiesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess?: () => void;
}

export default function TransformEntitiesSidebar({
  isOpen,
  onClose,
  projectId,
  onSuccess
}: TransformEntitiesSidebarProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const { user } = useUser();
  const { language } = useUserProfile();
  const isDarkMode = theme === 'dark';

  const [userPrompt, setUserPrompt] = useState('');
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<RecordType[]>(['URS']);
  const [dryRun, setDryRun] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Available entity types
  const availableEntityTypes: RecordType[] = ['URS', 'FS', 'TEST', 'RISK', 'SOP'];

  // Get localized entity type names
  const getEntityTypeName = (type: RecordType): string => {
    const nameMap: Record<RecordType, string> = {
      'URS': t('project_page_tab_urs') || 'Requirements',
      'FS': t('project_page_tab_fs') || 'Specifications',
      'RISK': t('project_page_tab_ra') || 'Risks',
      'TEST': t('project_page_tab_tests') || 'Tests',
      'SOP': t('project_page_tab_sops') || 'SOPs',
    };
    return nameMap[type] || type;
  };

  const handleEntityTypeToggle = (type: RecordType) => {
    setSelectedEntityTypes(prev => {
      if (prev.includes(type)) {
        // Don't allow deselecting all types
        if (prev.length === 1) {
          showToast(
            t('transform_entities_at_least_one_type') || 'Please select at least one entity type',
            'warning'
          );
          return prev;
        }
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleTransform = async () => {
    if (!userPrompt.trim()) {
      showToast(
        t('transform_entities_prompt_required') || 'Please enter a transformation prompt',
        'error'
      );
      return;
    }

    if (selectedEntityTypes.length === 0) {
      showToast(
        t('transform_entities_at_least_one_type') || 'Please select at least one entity type',
        'error'
      );
      return;
    }

    if (!user?.uid) {
      showToast(
        t('transform_entities_user_not_authenticated') || 'User not authenticated',
        'error'
      );
      return;
    }

    setIsLoading(true);
    showToast(
      t('transform_entities_starting') || 'Starting transformation...',
      'info'
    );

    try {
      // Use the deployed function URL
      const functionUrl = process.env.NEXT_PUBLIC_TRANSFORM_ENTITIES_URL || 
        'https://europe-west3-thescon-app.cloudfunctions.net/transformEntities';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          userPrompt: userPrompt.trim(),
          entityTypes: selectedEntityTypes,
          language: language || 'en',
          dryRun,
        }),
      });

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        if (dryRun) {
          const count = result.entitiesTransformed || 0;
          showToast(
            (t('transform_entities_preview_success') || 
            `Preview generated for ${count} entities. Review the changes and disable "Dry Run" to apply them.`).replace('{count}', String(count)),
            'success'
          );
        } else {
          const count = result.entitiesTransformed || 0;
          showToast(
            (t('transform_entities_success') || 
            `Successfully transformed ${count} entities.`).replace('{count}', String(count)),
            'success'
          );

          // Reset form
          setUserPrompt('');
          setSelectedEntityTypes(['URS']);
          setDryRun(true);

          // Call success callback
          if (onSuccess) {
            onSuccess();
          }

          // Close sidebar after a short delay
          setTimeout(() => {
            onClose();
          }, 1500);
        }
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Entity transformation error:', error);
      showToast(
        t('transform_entities_error') || `Failed to transform entities: ${errorMessage}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <img 
            src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} 
            alt="Thescon" 
            width={16} 
            height={16} 
          />
          <span>|</span>
          <h2>AI</h2>
        </div>

        {/* Prompt Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('transform_entities_prompt_label') || 'Transformation Prompt'}
          </h3>
          <p className={styles.sectionDescription}>
            {t('transform_entities_prompt_description') || 
              'Describe what transformation you want to apply to your entities. For example: "Replace the word \'legacy\' with \'deprecated\' throughout all entities" or "Rephrase all entities to follow more professional wording".'}
          </p>
          <GlobalTextarea
            label={t('transform_entities_prompt_label') || 'Transformation Prompt'}
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            placeholder={t('transform_entities_prompt_placeholder') || 
              'Enter your transformation instructions...'}
            disabled={isLoading}
            rows={6}
          />
        </div>

        {/* Entity Types Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('transform_entities_entity_types') || 'Entity Types'}
          </h3>
          <p className={styles.sectionDescription}>
            {t('transform_entities_select_types') || 
              'Select which entity types to transform:'}
          </p>
          <div className={styles.checkboxGroup}>
            {availableEntityTypes.map((type) => (
              <GlobalCheckbox
                key={type}
                checked={selectedEntityTypes.includes(type)}
                onChange={() => handleEntityTypeToggle(type)}
                disabled={isLoading}
                label={`${getEntityTypeName(type)} (${type})`}
              />
            ))}
          </div>
        </div>

        {/* Dry Run Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('transform_entities_dry_run') || 'Dry Run'}
          </h3>
          <p className={styles.sectionDescription}>
            {t('transform_entities_dry_run_description') || 
              'When enabled, you will see a preview of the changes without actually modifying your entities. Disable this to apply the transformations.'}
          </p>
          <div className={styles.checkboxGroup}>
            <GlobalCheckbox
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              disabled={isLoading}
              label={t('transform_entities_dry_run_enabled') || 'Enable Dry Run (Preview Only)'}
            />
          </div>
        </div>

        {/* Fire Button */}
        <div className={styles.actionSection}>
          <GlobalButton
            onClick={handleTransform}
            disabled={isLoading || !userPrompt.trim() || selectedEntityTypes.length === 0}
            isLoading={isLoading}
          >
            {isLoading 
              ? (t('transform_entities_processing') || 'Processing...')
              : dryRun
                ? (t('transform_entities_preview') || 'Preview Changes')
                : (t('transform_entities_apply') || 'Apply Transformations')}
          </GlobalButton>
        </div>

        <LocalLLMTrust />
      </div>
    </GlobalSidebar>
  );
}
